import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { BarChart2, Clock, Pause, Pencil, Play } from 'lucide-react';
import { Breadcrumbs, BreadcrumbItem, Container, PageHeader, Section } from '@vnetwork/ui/layout';
import { StatusPill, DataTable, EmptyState } from '@vnetwork/ui/data';
import { CompactStat, CompactStatGroup } from '../../components/CompactStat';
import { Button, Badge, Skeleton, Card, CardBody } from '@vnetwork/ui/primitives';
import { Chart } from '@vnetwork/ui/charts';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import {
  useWatchpoint,
  useWatchpointStats,
  useWatchpointMetrics,
  useWatchpointBaseline,
  useToggleWatchpoint,
} from '@/api/healthcheck';
import type { ConditionRow } from '../alarms/condition-serializer';
import { BaselinePanel } from './BaselinePanel';
import { WatchpointEditDialog } from './WatchpointEditDialog';
import {
  typeMeta,
  statusTone,
  statusLabel,
  targetSummary,
  fmtPercent,
  fmtLatency,
  fmtSeconds,
} from '../shared';
import { historyColumns, buildTrendChart, buildStatusCodeChart } from './ProbeHistory';
import { WatchpointAlarmsSection } from '../alarms/WatchpointAlarmsSection';

export function WatchpointDetail() {
  const { projectId, watchpointId } = useParams<{ projectId: string; watchpointId: string }>();
  const base = `/services/healthcheck/${projectId}`;
  const { show } = useToast();

  const { data: wp, isLoading, isError, refetch } = useWatchpoint(watchpointId);
  const { data: stats } = useWatchpointStats(watchpointId);
  const { data: metrics = [] } = useWatchpointMetrics(watchpointId);
  const { data: baseline } = useWatchpointBaseline(watchpointId);
  const toggle = useToggleWatchpoint(projectId ?? '');
  const [editOpen, setEditOpen] = useState(false);
  // "Apply as threshold" from the baseline panel → opens the alarm form pre-seeded.
  const [alarmPrefill, setAlarmPrefill] = useState<{ rows: ConditionRow[]; nonce: number } | null>(null);

  // The trend metric + history columns adapt to the monitor type (tls → cert
  // expiry, dns → resolved records, elastic → matched groups, protocol → latency).
  const type = wp?.type ?? 'http';
  const trend = useMemo(() => buildTrendChart(type, metrics), [type, metrics]);
  const codes = useMemo(() => buildStatusCodeChart(type, metrics), [type, metrics]);
  const cols = useMemo(() => historyColumns(type), [type]);

  if (isError) {
    return (
      <Container size="xl">
        <Alert
          tone="danger"
          title="Couldn't load monitor"
          actions={
            <Button size="sm" variant="secondary" onClick={() => refetch()}>
              Retry
            </Button>
          }
        >
          This monitor is unavailable.{' '}
          <Link to={`${base}/monitors`} style={{ color: 'var(--vn-primary-text)' }}>
            Back to monitors
          </Link>
        </Alert>
      </Container>
    );
  }

  if (isLoading || !wp) {
    return (
      <Container size="xl">
        <Skeleton style={{ height: 40, width: 280, marginBottom: 'var(--vn-space-4)' }} />
        <Skeleton style={{ height: 260 }} />
      </Container>
    );
  }

  const meta = typeMeta(wp.type);
  const Icon = meta.icon;

  return (
    <Container size="xl">
      <PageHeader
        icon={<Icon size={22} />}
        breadcrumbs={
          <Breadcrumbs>
            <BreadcrumbItem asChild><Link to={base}>Monitoring</Link></BreadcrumbItem>
            <BreadcrumbItem asChild><Link to={`${base}/monitors`}>Monitors</Link></BreadcrumbItem>
            <BreadcrumbItem current>{wp.name}</BreadcrumbItem>
          </Breadcrumbs>
        }
        title={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
            {wp.name}
            <Badge tone="neutral" appearance="subtle" size="sm">{meta.label}</Badge>
          </span>
        }
        description={targetSummary(wp.type, wp.config)}
        actions={
          <div style={{ display: 'flex', gap: 'var(--vn-space-2)' }}>
            <Button
              variant="secondary"
              size="sm"
              startIcon={<Pencil size={15} />}
              onClick={() => setEditOpen(true)}
            >
              Edit
            </Button>
            <Button
              variant="secondary"
              size="sm"
              startIcon={wp.is_active ? <Pause size={15} /> : <Play size={15} />}
              onClick={() =>
                toggle.mutate(
                  { id: wp.id, is_active: !wp.is_active },
                  { onError: (e) => show({ tone: 'danger', title: (e as Error)?.message || 'Update failed' }) },
                )
              }
            >
              {wp.is_active ? 'Pause' : 'Resume'}
            </Button>
          </div>
        }
      />

      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <CompactStatGroup columns={4}>
          <CompactStat
            label="Status"
            value={<StatusPill tone={statusTone(wp.is_active ? wp.last_status : null)}>{statusLabel(wp.last_status, wp.is_active)}</StatusPill>}
          />
          <CompactStat label="Uptime (24h)" value={fmtPercent(stats?.uptime_percent)} hint={`${stats?.total_checks ?? 0} checks`} />
          <CompactStat label="Avg latency" value={fmtLatency(stats?.avg_latency_ms)} hint={`max ${fmtLatency(stats?.max_latency_ms)}`} />
          <CompactStat label="Check interval" value={fmtSeconds(wp.check_interval)} hint={`timeout ${fmtSeconds(wp.timeout_seconds)}`} />
        </CompactStatGroup>

        <Section style={{ marginBottom: 0 }} title={trend?.label ?? 'Trend'} description="Signal per check over the recent window.">
          <Card flat>
            <CardBody>
              {!trend ? (
                <EmptyState icon={<BarChart2 size={20} />} title="No data yet" description="The trend appears once the monitor has run a few checks." size="sm" />
              ) : (
                <Chart
                  type={trend.kind}
                  data={trend.data}
                  xKey="t"
                  series={[{ key: trend.yKey, label: trend.label }]}
                  height={240}
                  valueFormatter={trend.format}
                />
              )}
            </CardBody>
          </Card>
        </Section>

        {codes && (
          <Section style={{ marginBottom: 0 }} title="HTTP status codes" description="Response class per check over the recent window.">
            <Card flat>
              <CardBody>
                <Chart
                  type="bar"
                  data={codes.data}
                  xKey="t"
                  series={codes.series}
                  stacked
                  showYAxis={false}
                  showLegend
                  height={200}
                  valueFormatter={(v) => (v ? '1 check' : '')}
                />
              </CardBody>
            </Card>
          </Section>
        )}

        <Section style={{ marginBottom: 0 }} title="Check history" description="Most recent probe results.">
          <DataTable
            columns={cols}
            data={metrics}
            getRowId={(m) => m.id}
            emptyState={<EmptyState icon={<Clock size={20} />} title="No checks yet" description="Results appear after the first scheduled run." size="sm" />}
          />
        </Section>

        <Section style={{ marginBottom: 0 }} title="Baseline & suggested thresholds" description="7-day rolling baseline. Apply a suggested value to create a fixed-threshold alarm.">
          <BaselinePanel
            watchpointType={wp.type}
            baseline={baseline}
            metrics={metrics}
            dimension={(wp.config as { dimensions?: string[] } | undefined)?.dimensions?.[0]}
            onApplyThreshold={(rows) => setAlarmPrefill({ rows, nonce: Date.now() })}
          />
        </Section>

        <Section style={{ marginBottom: 0 }} title="Alert rules" description="Alarms configured on this monitor.">
          <WatchpointAlarmsSection watchpointId={wp.id} watchpointType={wp.type} prefill={alarmPrefill ?? undefined} />
        </Section>
      </div>

      <WatchpointEditDialog
        watchpoint={editOpen ? wp : null}
        projectId={projectId ?? ''}
        onClose={() => setEditOpen(false)}
      />
    </Container>
  );
}

